import { type BaseQueryFn } from '@reduxjs/toolkit/query';
import { type AxiosError, type AxiosRequestConfig } from 'axios';

import { DEFAULT_TIMEOUT } from '../constants';
import { checkEnvironment } from '../utils';

import axiosService from './axiosInterceptor';

interface BaseQuery {
  baseUrl?: string;
  defaultConfig?: AxiosRequestConfig;
}

/**
 * Creates a custom Axios-based query function for RTK Query.
 *
 * This function wraps Axios to provide a consistent request mechanism with error handling.
 * It supports dynamic URL construction, HTTP methods, request data, query parameters, and custom configurations.
 *
 * @param {BaseQuery} options - Configuration options for the base query.
 * @returns {BaseQueryFn} A function that can be used with RTK Query's `baseQuery` option.
 */
export const axiosBaseQuery =
  (
    { baseUrl, defaultConfig }: BaseQuery = {
      baseUrl: '',
      defaultConfig: {
        timeout: DEFAULT_TIMEOUT,
      },
    }
  ): BaseQueryFn<{
    url: string;
    method?: AxiosRequestConfig['method'];
    data?: unknown;
    params?: Record<string, unknown>;
    config?: AxiosRequestConfig;
    isECMv6?: boolean;
  }> =>
  /**
   * Executes an API request using Axios.
   *
   * @param {Object} args - Request parameters.
   * @param {string} args.url - API endpoint relative to the base URL.
   * @param {string} [args.method='GET'] - HTTP method (GET, POST, PUT, DELETE, etc.).
   * @param {unknown} [args.data] - Request body data.
   * @param {Record<string, unknown>} [args.params] - Query parameters.
   * @param {AxiosRequestConfig} [args.config] - Additional Axios request configuration.
   * @returns {Promise<{ data?: unknown; error?: { status: number; data: unknown } }>} The API response or error.
   */
  async ({ url, method = 'GET', data, params, config, isECMv6 }) => {
    let URL: string;
    const { GATEWAY_API_URL } = checkEnvironment();

    switch (true) {
      case !!baseUrl:
        URL = baseUrl + url;
        break;
      case isECMv6:
        URL = (GATEWAY_API_URL ?? '') + url;
        break;
      default:
        URL = url;
        break;
    }

    try {
      // Executes the HTTP request using the provided configurations
      const result = await axiosService({
        url: URL,
        method,
        data,
        params,
        ...defaultConfig,
        ...config,
      });

      // Ensures the response contains data, otherwise throws an error
      if (result.data === undefined) {
        throw new Error('Invalid API response: data is undefined');
      }

      // Attach filename from Content-Disposition for blob responses, if available
      const responseData = result.data as unknown;
      const headers = result.headers as Record<string, string | undefined>;

      if (responseData instanceof Blob) {
        const contentDisposition =
          headers['content-disposition'] ?? headers['Content-Disposition'];

        if (contentDisposition) {
          // filename*=UTF-8''... or filename="..."
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
            contentDisposition
          );
          const rawName = match?.[1] ?? match?.[2] ?? '';

          // CWE-755: decodeURIComponent can throw on malformed encoding; fallback to raw
          const decodedName = (() => {
            try {
              return decodeURIComponent(rawName);
            } catch {
              return rawName;
            }
          })();

          try {
            const blobWithName = responseData as Blob & { fileName?: string };

            blobWithName.fileName = decodedName;
          } catch {
            // ignore if cannot assign
          }
        }
      }

      return { data: responseData };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      const responseData = err.response?.data;

      return {
        error: {
          status: err.response?.status ?? 500, // Defaults to 500 if no status is available
          // Previously: `err.response?.data || err.message ? err : …` parsed as
          // `(err.response?.data || err.message) ? err : …`, so a truthy JSON body
          // replaced `data` with the entire AxiosError (surfacing axios `message` to UI).
          data:
            responseData != null
              ? responseData
              : err.message || 'Unknown error',
        },
      };
    }
  };

export default axiosBaseQuery;
